import type { ReportFinding } from "../types/reports";
import { getConditionAnalysisRoot } from "./conditionAnalysisSelectors";
import { getEvidenceDocumentRoot } from "./reportEvidencePointer";
import { formatDisplayCurrency } from "./formatDisplayCurrency";
import { getSyncedDisplayCurrency } from "./displayCurrencySync";

export type ReportCardRangeStats = {
  totalTrades: number;
  winRate: number;
  totalPnl: number;
};

function toFiniteNumber(v: unknown): number | undefined {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

/** Snapshot `trades` are usually backend-shaped (`net_pnl`); aggregate without assuming frontend Trade shape. */
function computeStatsFromRawTrades(trades: unknown[]): ReportCardRangeStats {
  let totalTrades = 0;
  let closedCount = 0;
  let winCount = 0;
  let totalPnl = 0;

  for (const raw of trades) {
    if (!raw || typeof raw !== "object") continue;
    totalTrades++;
    const o = raw as Record<string, unknown>;
    const st = String(o.status ?? "").toLowerCase();
    if (st !== "closed") continue;

    closedCount++;
    const net = toFiniteNumber(o.net_pnl) ?? toFiniteNumber(o.netPnL) ?? 0;
    totalPnl += net;
    if (net > 0) winCount++;
  }

  const winRate = closedCount > 0 ? (winCount / closedCount) * 100 : 0;

  return {
    totalTrades,
    winRate: Math.round(winRate * 100) / 100,
    totalPnl: Math.round(totalPnl * 100) / 100,
  };
}

export function extractReportFindingTitles(findings: unknown, maxTitles = 5): string[] {
  if (!Array.isArray(findings)) return [];
  const out: string[] = [];
  for (const raw of findings) {
    if (out.length >= maxTitles) break;
    if (!raw || typeof raw !== "object") continue;
    const rawTitle = (raw as ReportFinding).title;
    const title = typeof rawTitle === "string" ? rawTitle.trim() : "";
    if (title) out.push(title);
  }
  return out;
}

function toRangeStatsFromObject(value: unknown): ReportCardRangeStats | null {
  if (!value || typeof value !== "object") return null;
  const o = value as Record<string, unknown>;
  const totalTrades = toFiniteNumber(o.total_trades) ?? toFiniteNumber(o.totalTrades) ?? toFiniteNumber(o.sample_size);
  const winRate = toFiniteNumber(o.win_rate) ?? toFiniteNumber(o.winRate);
  const totalPnl = toFiniteNumber(o.total_pnl) ?? toFiniteNumber(o.totalPnl) ?? toFiniteNumber(o.net_pnl);

  if (totalTrades == null && winRate == null && totalPnl == null) return null;
  return {
    totalTrades: Math.max(0, Math.round(totalTrades ?? 0)),
    winRate: Math.round((winRate ?? 0) * 100) / 100,
    totalPnl: Math.round((totalPnl ?? 0) * 100) / 100,
  };
}

/**
 * Find summary-like stats in common report bundle shapes.
 * This keeps cards populated across report types that do not include condition_analysis.
 */
function findStatsFallback(doc: Record<string, unknown>): ReportCardRangeStats | null {
  const directCandidates: unknown[] = [
    doc,
    doc.stats,
    (doc.stats as Record<string, unknown> | undefined)?.aggregated,
    (doc.stats as Record<string, unknown> | undefined)?.summary,
    (doc.stats as Record<string, unknown> | undefined)?.overview,
    (doc.stats as Record<string, unknown> | undefined)?.totals,
  ];

  for (const candidate of directCandidates) {
    const parsed = toRangeStatsFromObject(candidate);
    if (parsed) return parsed;
  }

  // Last resort: shallow recursive scan for any object containing the summary fields.
  const queue: Array<{ node: unknown; depth: number }> = [{ node: doc.stats, depth: 0 }];
  const seen = new Set<unknown>();
  while (queue.length) {
    const current = queue.shift();
    if (!current) continue;
    const { node, depth } = current;
    if (!node || typeof node !== "object" || seen.has(node)) continue;
    seen.add(node);

    const parsed = toRangeStatsFromObject(node);
    if (parsed) return parsed;
    if (depth >= 4) continue;

    for (const child of Object.values(node as Record<string, unknown>)) {
      if (child && typeof child === "object") {
        queue.push({ node: child, depth: depth + 1 });
      }
    }
  }

  return null;
}

/**
 * Prefer backend-provided aggregate stats for the exact report window.
 * Only fall back to trade-list reconstruction when aggregate stats are unavailable.
 */
export function extractReportRangeStats(snapshot: unknown): ReportCardRangeStats | null {
  const doc = getEvidenceDocumentRoot(snapshot) as Record<string, unknown> | null | undefined;
  if (!doc) return null;

  // 1) Canonical stats summary from backend payload (report-window scoped).
  const summaryStats = findStatsFallback(doc);
  if (summaryStats) {
    return summaryStats;
  }

  // 2) Condition-analysis aggregate (also backend-computed when available).
  const node = getConditionAnalysisRoot(doc);
  const agg = node?.regime_summary?.uniform_segment_context?.segment_aggregate;
  if (agg && !(agg.sample_size == null && agg.win_rate == null && agg.total_pnl == null)) {
    return {
      totalTrades: Math.max(0, Math.round(agg.sample_size ?? 0)),
      winRate: typeof agg.win_rate === "number" && Number.isFinite(agg.win_rate) ? agg.win_rate : 0,
      totalPnl: typeof agg.total_pnl === "number" && Number.isFinite(agg.total_pnl) ? agg.total_pnl : 0,
    };
  }

  // 3) Last resort: reconstruct from embedded trades list (may be limited/truncated).
  const tradeList = doc.trades;
  if (Array.isArray(tradeList) && tradeList.length > 0) {
    return computeStatsFromRawTrades(tradeList);
  }

  return null;
}

export function formatReportCardCurrency(value: number): string {
  return formatDisplayCurrency(value, getSyncedDisplayCurrency(), { locale: "en-US" });
}
